'use client';
import { createContext, useContext, useMemo } from 'react';
import { type Lang, type MarketTerms, DEFAULT_WEB } from '@lms/i18n';
import { makeT, type T } from './i18n-core';

/** 클라이언트 문맥 — 서버 layout 이 심고 클라 컴포넌트가 useT/useSite 로 읽는다 */
export interface SiteClientContext {
  lang: Lang;
  terms: MarketTerms | null;
  marketName: string | null;
  loggedIn: boolean;
  /** PC 재생을 Shell 앱으로 여나(설정>플레이어) — 재생 단추가 팝업이냐 앱이냐를 이 값으로 가른다 */
  pcApp: boolean;
}

const Ctx = createContext<SiteClientContext>({ lang: DEFAULT_WEB, terms: null, marketName: null, loggedIn: false, pcApp: false });

export function SiteProvider({ value, children }: { value: SiteClientContext; children: React.ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSite = () => useContext(Ctx);

/**
 * 번역 함수 — **문맥이 그대로면 같은 함수를 돌려준다**(2026-08-22).
 *
 * 전에는 렌더마다 `makeT` 를 새로 불러 매번 다른 함수가 나왔다. 이걸 `useCallback`·
 * `useEffect` 의 의존성에 넣은 곳(명령 팔레트)에서 **의존성이 매 렌더 바뀌어 효과가
 * 다시 돌고 → 상태를 바꾸고 → 또 렌더** 하는 무한 루프가 났다(사용자 제보: 검색 무한 에러).
 * 훅이 주는 값은 의존성에 들어갈 수 있어야 하므로 여기서 고정한다.
 */
export function useT(): T {
  const { lang, terms } = useContext(Ctx);
  return useMemo(() => makeT(lang, terms), [lang, terms]);
}
